import { useRef } from "react";
import type { ChatEvent, ProvisionalChatEvent, TokenUsage } from "@rivets/core";
import { useChatStore, selectChatStream, type ProvisionalEntry } from "./chatStore";
import { createFoldState, foldProvisionalSuffix, foldStep, type FoldState } from "./foldChatEvents";

export function useChatBlocks(chatId: string) {
  return useChatFold(chatId).blocks;
}

/** The whole fold, for the views that need what the blocks cannot say — the
 * background tasks still holding a run open. */
export function useChatFold(chatId: string): FoldState {
  const stream = useChatStore(selectChatStream(chatId));
  const events = stream.events;
  const provisional = stream.provisional;
  const ref = useRef<{
    chatId: string;
    state: FoldState;
    provisional: readonly ProvisionalEntry[];
    withProvisional: FoldState;
  }>({ chatId, state: createFoldState(), provisional: [], withProvisional: createFoldState() });

  if (ref.current.chatId !== chatId) {
    const state = createFoldState();
    ref.current = { chatId, state, provisional: [], withProvisional: state };
  }
  const confirmed = foldStep(ref.current.state, events);
  // The suffix is refolded only when either side moved; a render for any
  // other reason reuses the last result and its block identities.
  if (confirmed !== ref.current.state || provisional !== ref.current.provisional) {
    ref.current.withProvisional = provisional.length === 0
      ? confirmed
      : foldProvisionalSuffix(confirmed, events, provisionalEvents(provisional));
  }
  ref.current.state = confirmed;
  ref.current.provisional = provisional;
  return ref.current.withProvisional;
}

function provisionalEvents(entries: readonly ProvisionalEntry[]): ProvisionalChatEvent[] {
  return entries.map((entry) => entry.event);
}

/** What the last finished turn reported about the context window, or undefined
 * while no turn in this chat has completed with usage attached.
 *
 * Deliberately not folded out of `useChatBlocks`: the composer needs this and
 * the transcript needs the blocks, and running the full block fold twice per
 * delta for a long conversation costs far more than this scan. Like the fold,
 * it walks only the events appended since the last render, and rebuilds when
 * the store splices an older replay page in ahead of the frontier. */
export function useLatestUsage(chatId: string | undefined): TokenUsage | undefined {
  const events = useChatStore(selectChatStream(chatId ?? "")).events;
  const ref = useRef<{ chatId: string | undefined; processedCount: number; frontierSeq: number; usage?: TokenUsage }>({
    chatId,
    processedCount: 0,
    frontierSeq: 0,
  });

  if (ref.current.chatId !== chatId) {
    ref.current = { chatId, processedCount: 0, frontierSeq: 0 };
  }

  const boundary = ref.current.processedCount > 0 ? events[ref.current.processedCount - 1] : undefined;
  if (ref.current.processedCount > 0 && boundary?.seq !== ref.current.frontierSeq) {
    ref.current = { chatId, processedCount: 0, frontierSeq: 0 };
  }

  for (let index = ref.current.processedCount; index < events.length; index++) {
    const usage = completedTurnUsage(events[index]);
    if (usage) ref.current.usage = usage;
  }
  ref.current.processedCount = events.length;
  ref.current.frontierSeq = events[events.length - 1]?.seq ?? ref.current.frontierSeq;

  return ref.current.usage;
}

function completedTurnUsage(event: ChatEvent | undefined): TokenUsage | undefined {
  if (!event || event.payload.kind !== "agent") return undefined;
  const agentEvent = event.payload.event;
  return agentEvent.type === "turn.completed" ? agentEvent.usage : undefined;
}
